import { BaseService, normalizePrimaryResourceId } from '@/chassis/data/services/base-service';
import { BaseFormService, ValidationRules, type FormState } from '@/chassis/data/services/base-form-service';
import { buildDefaults, buildRequiredRules, buildSanitizer, type FieldConfigMap } from '@/chassis/data/services/field-config';
import { StringUtils, NumberUtils, ValueUtils } from '@/chassis/core/utils';
import { normalizeTenderStatus, normalizeTenderModality, supplierRegionalityBenefitOptions, supplierSizeBenefitOptions, type StatusKey } from '../constants';
import { httpRequest, resolveApiUrl } from '@/chassis/data/services/http-client';
import { i18n } from '../i18n';

// === RN-PUB-MEPP / RN-PUB-REG: maps PT↔EN ===
// Options do dropdown (constants.ts) usam values em PT por legado de i18n,
// mas BE espera enums em EN (ex: 'INHERIT', 'ME_EPP_COOP_PREFERENCE').
// Sem este mapping, BE persiste 'HERDAR' e o gate de empate ficto ME/EPP
// (ME_EPP_ENABLED_BENEFITS) nunca casa. Mesmo padrão de Lots/service.ts:175-204.
const REGIONALITY_PT_TO_EN: Record<string, string> = {
    'HERDAR': 'INHERIT',
    'NÃO': 'NONE',
    'PREFERÊNCIA ESTADUAL': 'STATE_PREFERENCE',
    'EXCLUSIVO ESTADUAL': 'STATE_EXCLUSIVE',
    'PREFERÊNCIA MUNICIPAL': 'MUNICIPAL_PREFERENCE',
    'EXCLUSIVO MUNICIPAL': 'MUNICIPAL_EXCLUSIVE',
};
const SIZE_PT_TO_EN: Record<string, string> = {
    'HERDAR': 'INHERIT',
    'NÃO': 'NONE',
    'PREFERÊNCIA ME/EPP/COOP': 'ME_EPP_COOP_PREFERENCE',
    'EXCLUSIVO ME/EPP/COOP': 'ME_EPP_COOP_EXCLUSIVE',
};
const invertMap = (m: Record<string, string>): Record<string, string> =>
    Object.fromEntries(Object.entries(m).map(([k, v]) => [v, k]));
const REGIONALITY_EN_TO_PT = invertMap(REGIONALITY_PT_TO_EN);
const SIZE_EN_TO_PT = invertMap(SIZE_PT_TO_EN);
/** Aplica o map; se valor já estiver no destino (ou for desconhecido), passa cru. Idempotente. */
const mapBenefit = (m: Record<string, string>, v: unknown): string | undefined => {
    const raw = v == null ? undefined : typeof v === 'string' ? v : String(v);
    const s = StringUtils.toOptionalTrimmed(raw);
    if (!s) return s;
    return m[s] ?? s;
};

/**
 * ATENCAO: este tipo e DUPLICADO em
 * `chassis/ui/components/data-input/AdditionalDocumentsField`. Os dois precisam
 * andar juntos — em 21/10/2026 o `id` foi adicionado so la e o `fetchOne` daqui
 * nao compilou (TS2353). Mexeu em um, mexe no outro.
 */
export interface AdditionalDocumentEntry {
    /**
     * Id do documento ja gravado (uploadeddocuments). So existe em documento que
     * veio do servidor; arquivo recem-escolhido ainda nao tem. E o que permite a
     * remocao chegar ate `additionalDocumentIds` — ver fetchAdditionalDocuments.
     */
    id?: string;
    name?: string;
    file?: File;
    url?: string;
}

// Campos compartilhados entre form/DTO/record
export interface PublicTenderCoreFields {
    contractingAgencyId?: string;
    title: string;
    description?: string;
    yearOfReference?: number;
    regulationUsed?: string;
    publicTenderCode: string;
    administrativeProcessCode: string;
    pcaReference?: string;
    status?: string;
    contractType?: string;
    fundingSource?: string;
    modality?: string;
    judgementCriteria?: string;
    /** Peso da técnica/preço (%) — só em técnica e preço (Art. 36-37). */
    technicalScoreWeight?: number;
    priceScoreWeight?: number;
    disputeType?: string;
    /** RN-S1.1.4: AUTO (scheduler abre lotes) ou MANUAL (pregoeiro clica play). Default AUTO. */
    disputeStartMode?: string;
    /** RN-S1.1.4: '1'|'2'|'3'|'5'|'10'|'ALL' — quantos lotes abrem simultaneamente em modo AUTO. Default ALL. */
    batchSize?: string;
    supplierRegionalityBenefit?: string;
    supplierSizeBenefit?: string;
    /** T4 prioridade regional: margem (%) e municípios com prioridade (editável por edital). */
    regionalPriorityMarginPercent?: number;
    regionalPriorityMunicipalities?: string[];
    bidDecimalPlaces?: number;
    proposalValidityMonths?: number;
    documentSubmissionMode?: string;
    isHiddenBudget?: boolean;
    /** cdcb-96 (Art. 58 Lei 14.133): fornecedor anexa a garantia de proposta para enviar. */
    requiresProposalGuarantee?: boolean;
    isPhaseInversion?: boolean;
    /** 'SINGLE' (uma fase recursal, regra da Lei) | 'DOUBLE' (duas fases). So tem efeito com isPhaseInversion ligado. */
    appealPhaseMode?: string;
    /** Marco do ato "Tornar documentos publicos" (inversao de fases). */
    suppliersDisclosedAt?: string;
    /** Marco do ato "Tornar propostas publicas": libera os valores; o nome das
     *  empresas segue selado ate o julgamento, e a disputa nao abre sem ele. */
    proposalsDisclosedAt?: string;
    documentSubmissionDeadlineHours?: number;
    /** Dispensa Eletrônica (Art. 75 + IN 67/2021) — só quando modality='ELECTRONIC_EXEMPTION'. */
    exemptionLegalBasis?: string;
    exemptionJustification?: string;
    exemptionBidWindowHours?: number;
    /** WITH_BIDS (janela de lances) | NO_BIDS (so propostas; menor proposta vence). */
    exemptionDisputeMode?: 'WITH_BIDS' | 'NO_BIDS' | string;
    exemptionSimplifiedQualification?: boolean;
    /**
     * Credenciamento continuo (Art. 79, rito do cliente 15/09/2026): fim do ACOLHIMENTO
     * (ate 24 meses). Nao ha data de disputa — proposalSubmissionEndDate e
     * disputeStartDate sao derivadas desta (form + backend deriveAccreditationDates).
     */
    accreditationEndDate?: string;
    /**
     * Sessao virtual: transmissao ao vivo + registro do link da gravacao
     * (Chamamento 052/2026 Anapolis, itens 17.3.2 e 17.5.2). Campos
     * TRANSVERSAIS - valem em qualquer modalidade, nao so no credenciamento.
     */
    broadcastEnabled?: boolean;
    broadcastUrl?: string;
    broadcastRoomName?: string;
    broadcastLivestreamUrl?: string;
    broadcastRecordingUrl?: string;
    publishedDate?: string;
    pncpPublicationDeadline?: string;
    proposalSubmissionStartDate?: string;
    proposalSubmissionEndDate?: string;
    clarificationEndDate?: string;
    impeachmentEndDate?: string;
    disputeStartDate?: string;
    appealManifestationTime?: number;
    appealEndDate?: string;
    appealReceptionFormTimeHours?: number;
    counterArgumentsReceptionFormTimeHours?: number;
    supportTeam?: string[];
    auctioneer?: string;
    superiorAuthority?: string;
    contactPhone?: string;
    contactEmail?: string;
    noticeFileName?: string;
    persistedNoticeFileName?: string;
    noticeDocument?: AdditionalDocumentEntry;
    mainDocumentId?: string;
    previousMainDocumentId?: string;
    removeNoticeDocument?: boolean;
    additionalDocuments?: AdditionalDocumentEntry[]; // usado apenas no front, convertido em IDs no envio
    additionalDocumentIds?: string[];
    requiredProposalDocuments?: string[];
    active?: boolean;
    createdAt?: string;
    updatedAt?: string;
    createdBy?: string;
    createdByName?: string;
    createdByUser?: string;
    updatedBy?: string;
    updatedByName?: string;
    updatedByUser?: string;
}

export const resolveActiveFlag = (
    record: Partial<Pick<PublicTenderRecord, 'active' | 'status' | 'statusKey'>>,
    fallback = true,
) => {
    if (typeof record.active === 'boolean') return record.active;

    const normalizedStatus = normalizeTenderStatus(record.status ?? record.statusKey);
    if (normalizedStatus) return !['suspended', 'cancelled'].includes(normalizedStatus);

    return fallback;
};

// Form/DTO usam apenas os campos centrais
// Base record da API adiciona campos de leitura
export interface PublicTenderRecord extends PublicTenderCoreFields {
    id: string;
    contractingAgency?: string;
    mainDocumentId?: string;
    estimatedValue?: number;
    submissionDeadline?: string;
    openingDate?: string;
    publishedAt?: string;
    /** FIX19 — modo republicacao ligado na licitacao suspensa. Campo de LEITURA:
     *  fica fora do FIELD_CONFIG de proposito, para o save do formulario nunca
     *  mandar o valor antigo e desligar o modo sem querer. Quem liga e desliga e
     *  o botao "Republicar edital" (setRepublicationMode). */
    republicationMode?: boolean;
    proposalCount?: number;
    viewCount?: number;
    daysUntilDeadline?: number;
    urgency?: string;
    formattedValue?: string;
    canSubmitProposal?: boolean;
    isUrgent?: boolean;
    statusKey?: StatusKey;
    statusSlug?: string;
    modalityKey?: string;
    modalitySlug?: string;
    // Status da publicacao PNCP anexado pelo backend no GET /publictenders/:id
    pncpCode?: string;
    pncpPublicUrl?: string;
    pncpIntegrationStatus?: string;
}

type PublicTenderCreateDto = Omit<
    PublicTenderCoreFields,
    'createdAt' | 'updatedAt' | 'createdBy' | 'createdByName' | 'createdByUser' | 'updatedBy' | 'updatedByName' | 'updatedByUser'
>;
type PublicTenderUpdateDto = Partial<PublicTenderCreateDto>;

interface PublicTenderFilters {
    searchText?: string;
    modality?: string;
    category?: string;
    minValue?: number;
    maxValue?: number;
    urgentOnly?: boolean;
    active?: boolean;
    page?: number;
    limit?: number;
    /**
     * Um status ou uma lista. O chassis do backend converte array em `$in`
     * (base.listrecords.usecase#applyArrayToInFilters) e o http-client serializa
     * array como parâmetro repetido (`status=A&status=B`), então a multiseleção
     * do filtro roda server-side, sobre TODAS as páginas.
     */
    status?: string | string[];
    state?: string;
    municipality?: string;
    contractingAgencyId?: string;
    contractingAgency?: string;
    administrativeProcessCode?: string;
    publicTenderCode?: string;
    /** Campo de ordenacao server-side (ex: 'disputeStartDate'). BE: base.listrecords.usecase. */
    orderBy?: string;
    orderDirection?: 'asc' | 'desc';
}

interface TenderDetailsAggregate {
    tender: PublicTenderRecord;
    related?: any;
    summary?: any;
}

const FIELD_CONFIG: FieldConfigMap<PublicTenderCoreFields> = {
    contractingAgencyId: { label: 'Órgão promotor', required: true, defaultValue: undefined, sanitize: StringUtils.toRequiredTrimmed },
    title: { label: 'Título', required: true, defaultValue: '', sanitize: StringUtils.toRequiredTrimmed },
    description: { label: 'Descrição', defaultValue: '', sanitize: StringUtils.toOptionalTrimmed },
    yearOfReference: { label: 'Ano de referência', defaultValue: undefined, sanitize: NumberUtils.toNumberOrUndefined },
    regulationUsed: { label: 'Regulamento utilizado', defaultValue: undefined, sanitize: StringUtils.toOptionalTrimmed },
    publicTenderCode: { label: 'Número da licitação', required: true, defaultValue: '', sanitize: StringUtils.toRequiredTrimmed },
    administrativeProcessCode: { label: 'Processo administrativo', required: true, defaultValue: '', sanitize: StringUtils.toRequiredTrimmed },
    pcaReference: { label: 'Referência PCA', defaultValue: undefined, sanitize: StringUtils.toOptionalTrimmed },
    status: { label: 'Status', defaultValue: undefined, sanitize: StringUtils.toOptionalTrimmed },
    contractType: { label: 'Tipo de contrato', defaultValue: undefined, sanitize: StringUtils.toOptionalTrimmed },
    fundingSource: { label: 'Origem dos recursos', required: true, defaultValue: undefined, sanitize: StringUtils.toOptionalTrimmed },
    modality: { label: 'Modalidade', defaultValue: undefined, sanitize: StringUtils.toOptionalTrimmed },
    judgementCriteria: { label: 'Critério de julgamento', defaultValue: undefined, sanitize: StringUtils.toOptionalTrimmed },
    technicalScoreWeight: { label: 'Peso da técnica (%)', defaultValue: undefined, sanitize: NumberUtils.toNumberOrUndefined },
    priceScoreWeight: { label: 'Peso do preço (%)', defaultValue: undefined, sanitize: NumberUtils.toNumberOrUndefined },
    disputeType: { label: 'Tipo de disputa', defaultValue: undefined, sanitize: StringUtils.toOptionalTrimmed },
    // RN-S1.1.4: Inicio da disputa AUTO/MANUAL e batchSize. Default AUTO+ALL pra
    // backward compat com tenders historicos (cascade-all).
    disputeStartMode: { label: 'Início da disputa', defaultValue: 'AUTO', sanitize: StringUtils.toOptionalTrimmed },
    batchSize: { label: 'Lotes simultâneos', defaultValue: 'ALL', sanitize: StringUtils.toOptionalTrimmed },
    supplierRegionalityBenefit: { label: 'Benefício de regionalidade', defaultValue: supplierRegionalityBenefitOptions[0]?.value, sanitize: (v) => mapBenefit(REGIONALITY_PT_TO_EN, v) },
    supplierSizeBenefit: { label: 'Benefício por porte', defaultValue: supplierSizeBenefitOptions[0]?.value, sanitize: (v) => mapBenefit(SIZE_PT_TO_EN, v) },
    regionalPriorityMarginPercent: { label: 'Margem da prioridade regional (%)', defaultValue: undefined, sanitize: NumberUtils.toNumberOrUndefined },
    regionalPriorityMunicipalities: {
        label: 'Municípios com prioridade regional',
        defaultValue: undefined,
        sanitize: (v: unknown) => {
            if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
            if (typeof v === 'string' && v.trim()) return v.split(/[\n,;]/).map((s) => s.trim()).filter(Boolean);
            return undefined;
        },
    },
    bidDecimalPlaces: { label: 'Casas decimais', defaultValue: undefined, sanitize: NumberUtils.toNumberOrUndefined },
    proposalValidityMonths: { label: 'Validade da proposta (meses)', defaultValue: undefined, sanitize: NumberUtils.toNumberOrUndefined },
    documentSubmissionMode: { label: 'Modo de envio de documentação', required: true, defaultValue: 'FASE_JULGAMENTO', sanitize: StringUtils.toOptionalTrimmed },
    isHiddenBudget: { label: 'Orçamento sigiloso', defaultValue: false, sanitize: (v) => Boolean(v) },
    requiresProposalGuarantee: { label: 'Exige garantia de proposta', defaultValue: false, sanitize: (v) => Boolean(v) },
    isPhaseInversion: { label: 'Inversão de fases', defaultValue: false, sanitize: (v) => Boolean(v) },
    appealPhaseMode: { label: 'Fases recursais', defaultValue: 'SINGLE', sanitize: StringUtils.toOptionalTrimmed },
    documentSubmissionDeadlineHours: { label: 'Prazo de envio de documentação (h)', defaultValue: undefined, sanitize: NumberUtils.toNumberOrUndefined },
    // Dispensa Eletrônica (Art. 75 + IN 67/2021) — Fase 1 (21/07/2026)
    exemptionLegalBasis: { label: 'Hipótese legal (Art. 75)', defaultValue: undefined, sanitize: StringUtils.toOptionalTrimmed },
    exemptionJustification: { label: 'Justificativa da dispensa', defaultValue: undefined, sanitize: StringUtils.toOptionalTrimmed },
    exemptionBidWindowHours: { label: 'Janela de lances (h)', defaultValue: undefined, sanitize: NumberUtils.toNumberOrUndefined },
    exemptionDisputeMode: { label: 'Modo de disputa da dispensa', defaultValue: undefined, sanitize: StringUtils.toOptionalTrimmed },
    exemptionSimplifiedQualification: { label: 'Habilitação simplificada', defaultValue: false, sanitize: (v) => Boolean(v) },
    // Credenciamento continuo (15/09/2026)
    accreditationEndDate: { label: 'Fim do acolhimento', defaultValue: undefined },
    // Sessao virtual (transversal). defaultValue explicito: default declarado na
    // entity do backend nao e aplicado antes da validacao em alguns fluxos do
    // chassis, entao o campo vai sempre no payload.
    broadcastEnabled: { label: 'Transmissão da sessão', defaultValue: false, sanitize: (v) => Boolean(v) },
    broadcastLivestreamUrl: { label: 'Link da transmissão pública', defaultValue: undefined, sanitize: StringUtils.toOptionalTrimmed },
    broadcastRecordingUrl: { label: 'Link da gravação da sessão', defaultValue: undefined, sanitize: StringUtils.toOptionalTrimmed },
    publishedDate: { label: 'Data de publicação', defaultValue: undefined },
    proposalSubmissionStartDate: { label: 'Início de propostas', defaultValue: undefined },
    proposalSubmissionEndDate: { label: 'Fim de propostas', defaultValue: undefined },
    clarificationEndDate: { label: 'Fim de esclarecimentos', defaultValue: undefined },
    impeachmentEndDate: { label: 'Fim de impugnação', defaultValue: undefined },
    disputeStartDate: { label: 'Início da disputa', defaultValue: undefined },
    appealManifestationTime: { label: 'Tempo de manifestação de recurso', defaultValue: undefined, sanitize: NumberUtils.toNumberOrUndefined },
    appealEndDate: { label: 'Fim de recursos', defaultValue: undefined },
    appealReceptionFormTimeHours: { label: 'Prazo de recebimento de recurso (h)', defaultValue: undefined, sanitize: NumberUtils.toNumberOrUndefined },
    counterArgumentsReceptionFormTimeHours: { label: 'Prazo de contrarrazões (h)', defaultValue: undefined, sanitize: NumberUtils.toNumberOrUndefined },
    supportTeam: { label: 'Equipe de apoio', required: true, defaultValue: [] as string[], sanitize: (v: any) => Array.isArray(v) ? v.filter((s: any): s is string => typeof s === 'string' && s.trim().length > 0) : [] },
    // Label estatico — pra exibicao dinamica por modalidade (Pregoeiro/Leiloeiro/
    // Agente de Contratacao), use getRoleLabelByModality(tender.modality) no
    // momento da renderizacao. Mantemos 'Pregoeiro' como default por ser a
    // modalidade majoritaria (Pregao Eletronico).
    auctioneer: { label: 'Pregoeiro', defaultValue: undefined, sanitize: StringUtils.toOptionalTrimmed },
    superiorAuthority: { label: 'Autoridade superior', defaultValue: undefined, sanitize: StringUtils.toOptionalTrimmed },
    contactPhone: { label: 'Telefone', defaultValue: undefined, sanitize: StringUtils.toOptionalTrimmed },
    contactEmail: { label: 'Email', defaultValue: undefined, sanitize: StringUtils.toOptionalTrimmed },
    noticeFileName: { label: 'Edital', required: true, defaultValue: undefined, sanitize: StringUtils.toOptionalTrimmed },
    additionalDocuments: { label: 'Documentos adicionais', defaultValue: [] },
    additionalDocumentIds: { label: 'IDs dos documentos adicionais', defaultValue: [] },
    requiredProposalDocuments: { label: 'Documentos exigidos para habilitação do fornecedor', required: true, defaultValue: [], sanitize: StringUtils.toOptionalStringArray },
    active: { label: 'Ativo', defaultValue: true, sanitize: (v) => (typeof v === 'boolean' ? v : true) },
    createdAt: { label: 'Criado em', defaultValue: undefined },
    updatedAt: { label: 'Atualizado em', defaultValue: undefined },
    createdBy: { label: 'Criado por', defaultValue: undefined },
    createdByName: { label: 'Criado por (nome)', defaultValue: undefined },
    createdByUser: { label: 'Criado por (usuário)', defaultValue: undefined },
    updatedBy: { label: 'Atualizado por', defaultValue: undefined },
    updatedByName: { label: 'Atualizado por (nome)', defaultValue: undefined },
    updatedByUser: { label: 'Atualizado por (usuário)', defaultValue: undefined },
};

const sanitizeAdditionalDocuments = (docs?: AdditionalDocumentEntry[]) => {
    const cleaned = (docs ?? [])
        .map((doc) => ({
            name: StringUtils.toOptionalTrimmed(doc.name),
            file: doc.file,
        }))
        .filter((doc) => doc.name && doc.file);
    return cleaned.length > 0 ? cleaned : undefined;
};

const sanitizeRequiredProposalDocuments = (docs?: string[]) => {
    const cleaned = StringUtils.toOptionalStringArray(docs).filter(Boolean);
    const unique = Array.from(new Set(cleaned));
    return unique.length > 0 ? unique : undefined;
};

const sanitizeNoticeDocument = (doc?: AdditionalDocumentEntry) => {
    const file = doc?.file;
    if (!file) return undefined;

    return {
        name: StringUtils.toOptionalTrimmed(doc?.name) ?? file.name,
        file,
    };
};

const toStringList = (raw?: any): string[] | undefined => {
    const source = Array.isArray(raw) ? raw : undefined;
    if (!source) return undefined;

    const mapped = source
        .map((item: any) => {
            if (typeof item === 'string' || typeof item === 'number') return StringUtils.toOptionalTrimmed(String(item));
            if (item && typeof item === 'object') {
                const candidate = item.key ?? item.id ?? item._id ?? item.value;
                if (typeof candidate === 'string' || typeof candidate === 'number') return StringUtils.toOptionalTrimmed(String(candidate));
            }
            return undefined;
        })
        .filter(Boolean) as string[];

    return mapped.length ? mapped : undefined;
};

const publicTenderFormDefaults = buildDefaults(FIELD_CONFIG);
const sanitizeCoreFields = buildSanitizer(FIELD_CONFIG);

const fileToBase64 = async (file: File): Promise<string> =>
    await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = typeof reader.result === 'string' ? reader.result : '';
            const base64 = result.includes(',') ? result.split(',')[1] ?? '' : result;
            resolve(base64);
        };
        reader.onerror = () => reject(reader.error ?? new Error('Falha ao ler arquivo para upload.'));
        reader.readAsDataURL(file);
    });

const uploadAdditionalDocuments = async (docs?: AdditionalDocumentEntry[]) => {
    const cleaned = sanitizeAdditionalDocuments(docs);
    if (!cleaned || cleaned.length === 0) return [] as string[];

    const { uploadedDocumentsService } = await import('@/domains/pages/1.4-general-module-document/UploadedDocuments/service');

    // UM arquivo por requisicao.
    //
    // Antes iam TODOS num unico JSON, em base64 — que infla 33% —, e o lote
    // estourava o limite de corpo do servidor (15mb, base.initialization.ts:177).
    // O servidor devolvia 413 e o navegador mostrava "Failed to fetch", sem
    // resposta nenhuma: 20/10, tres tentativas seguidas de um orgao em producao.
    // Com um por vez, o que limita passa a ser o tamanho de CADA arquivo e nao
    // a soma deles — um edital isolado nao chega perto dos 15mb.
    //
    // Sequencial de proposito: em paralelo voltariam a disputar banda e memoria
    // do servidor ao mesmo tempo, que e o problema que se esta tirando.
    const ids: string[] = [];
    for (const doc of cleaned) {
        const response = await uploadedDocumentsService.uploadBatch({
            files: [{
                name: doc.name!,
                size: doc.file!.size,
                type: doc.file!.type,
                base64: await fileToBase64(doc.file!),
            }],
        });
        const uploaded = response?.data?.uploaded ?? [];
        const failed = response?.data?.failed ?? [];

        if (!uploaded.length) {
            const reason = failed.length
                ? failed.map((item: any) => `${item.name}: ${item.reason}`).join('; ')
                : response?.message ?? `Falha ao enviar "${doc.name}".`;
            throw new Error(reason || `Falha ao enviar "${doc.name}".`);
        }
        ids.push(...uploaded.map((d: any) => d.id).filter(Boolean));
    }

    return ids;
};

export const sanitizePublicTenderPayload = async (form: PublicTenderCoreFields): Promise<PublicTenderCreateDto> => {
    const base = sanitizeCoreFields(form) as PublicTenderCreateDto;
    const pendingNoticeDocument = sanitizeNoticeDocument(form.noticeDocument);

    // Backend expects appealReceptionTimeHours/counterArgumentsReceptionTimeHours; map from UI field names
    (base as any).appealReceptionTimeHours = NumberUtils.toNumberOrUndefined((form as any).appealReceptionFormTimeHours);
    (base as any).counterArgumentsReceptionTimeHours = NumberUtils.toNumberOrUndefined((form as any).counterArgumentsReceptionFormTimeHours);
    delete (base as any).appealReceptionFormTimeHours;
    delete (base as any).counterArgumentsReceptionFormTimeHours;

    // Inversao de fases: a disputa nunca abre por data — a habilitacao vem antes.
    // Forcado aqui, e nao so na tela, porque o que protege e o DADO: o backend tem a
    // trava definitiva no cascadeSessionsToBidding, e esta linha evita que o cadastro
    // fique gravado dizendo AUTO uma coisa que nunca vai acontecer.
    if ((form as any).isPhaseInversion === true) {
        (base as any).disputeStartMode = 'MANUAL';
    }

    // Keep BE compatibility while frontend standardizes on `active`
    const resolvedActive = resolveActiveFlag(form, publicTenderFormDefaults.active);
    (base as any).active = resolvedActive;

    const requiredProposalDocuments = sanitizeRequiredProposalDocuments((form as any).requiredProposalDocuments);
    (base as any).requiredProposalDocuments = requiredProposalDocuments;
    (base as any).noticeFileName = pendingNoticeDocument?.name ?? StringUtils.toOptionalTrimmed(form.noticeFileName);

    // Upload documentos adicionais e usar apenas IDs; preserva IDs existentes se nada novo for enviado
    const existingIds = Array.isArray((form as any).additionalDocumentIds)
        ? ((form as any).additionalDocumentIds as string[]).filter(Boolean)
        : undefined;
    const uploadedIds = await uploadAdditionalDocuments(form.additionalDocuments);
    // SOMA os novos aos que ja estavam, nao substitui.
    //
    // A versao anterior era `uploadedIds?.length ? uploadedIds : existingIds`: o
    // nome dizia "combined" mas nao combinava nada. Anexar UM documento numa
    // edicao desvinculava silenciosamente todos os anteriores da licitacao.
    const combinedIds = [...new Set([...(existingIds ?? []), ...uploadedIds])];
    (base as any).additionalDocumentIds = combinedIds.length ? combinedIds : undefined;

    return base;
};

const uploadMainTenderDocument = async (tenderId: string | number, document?: AdditionalDocumentEntry) => {
    const pendingNoticeDocument = sanitizeNoticeDocument(document);
    if (!pendingNoticeDocument?.file) return null;

    if (!pncpIntegrationEnabled) {
        const uploadedIds = await uploadAdditionalDocuments([
            {
                name: pendingNoticeDocument.name,
                file: pendingNoticeDocument.file,
            },
        ]);
        const uploadedDocumentId = uploadedIds[0];
        return uploadedDocumentId
            ? {
                  data: {
                      documentId: uploadedDocumentId,
                      mainDocumentId: uploadedDocumentId,
                  },
              }
            : null;
    }

    const cleanTenderId = String(tenderId).replace(/^PUBLICTENDER:/, '');
    const payload = new FormData();
    payload.append('file', pendingNoticeDocument.file, pendingNoticeDocument.name);
    payload.append('isMain', 'true');

    return httpRequest<any>(`/external/v1/public-tenders/${encodeURIComponent(cleanTenderId)}/documents`, {
        method: 'POST',
        rawBody: payload,
    });
};

const deleteTenderDocument = async (tenderId: string | number, documentId?: string | null) => {
    if (!pncpIntegrationEnabled) {
        return null;
    }

    const normalizedDocumentId = StringUtils.toOptionalTrimmed(documentId ?? undefined);
    if (!normalizedDocumentId) return null;

    const cleanTenderId = String(tenderId).replace(/^PUBLICTENDER:/, '');
    return httpRequest<any>(
        `/external/v1/public-tenders/${encodeURIComponent(cleanTenderId)}/documents/${encodeURIComponent(normalizedDocumentId)}`,
        { method: 'DELETE' },
    );
};

const linkMainDocumentToTender = async (tenderId: string | number, documentId: string, noticeFileName?: string) => {
    const cleanTenderId = String(normalizePrimaryResourceId(tenderId));
    return httpRequest<any>(`/publictenders/${encodeURIComponent(cleanTenderId)}`, {
        method: 'PUT',
        body: {
            mainDocumentId: documentId,
            ...(noticeFileName ? { noticeFileName } : {}),
        },
    });
};

const unlinkMainDocumentFromTender = async (tenderId: string | number) => {
    const cleanTenderId = String(normalizePrimaryResourceId(tenderId));
    return httpRequest<any>(`/publictenders/${encodeURIComponent(cleanTenderId)}`, {
        method: 'PUT',
        body: {
            mainDocumentId: null,
        },
    });
};

const resolveUploadedMainDocumentId = (uploadResponse: any): string | undefined =>
    StringUtils.toOptionalTrimmed(uploadResponse?.data?.documentId ?? uploadResponse?.data?.mainDocumentId);

const shouldDeletePreviousMainDocument = (
    previousMainDocumentId?: string,
    removeNoticeDocument?: boolean,
    hasNewNoticeFile?: boolean,
    uploadedMainDocumentId?: string,
): boolean =>
    Boolean(previousMainDocumentId) &&
    (Boolean(removeNoticeDocument) || (Boolean(hasNewNoticeFile) && previousMainDocumentId !== uploadedMainDocumentId));

const shouldUnlinkMainDocument = (uploadedMainDocumentId?: string, removeNoticeDocument?: boolean): boolean =>
    !uploadedMainDocumentId && Boolean(removeNoticeDocument);

export const fetchAdditionalDocuments = async (ids?: Array<string | number | Record<string, any>>): Promise<AdditionalDocumentEntry[]> => {
    if (!ids || !ids.length) return [];

    const normalizeId = (raw: any): string | undefined => {
        if (typeof raw === 'string' || typeof raw === 'number') return String(raw);
        if (raw && typeof raw === 'object') {
            const candidate = raw.id ?? raw._id;
            if (candidate !== undefined && candidate !== null) return String(candidate);
        }
        return undefined;
    };

    const normalizedIds = ids
        .map(normalizeId)
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0);

    if (!normalizedIds.length) return [];
    const { uploadedDocumentsService } = await import('@/domains/pages/1.4-general-module-document/UploadedDocuments/service');

    const fetchOne = async (id: string): Promise<AdditionalDocumentEntry | null> => {
        const fallbackUrl = resolveApiUrl(`/uploadeddocuments/${id}/download`);
        let name: string | undefined = undefined;
        let url = fallbackUrl;

        try {
            const meta = await uploadedDocumentsService.get(id);
            name = (meta as any)?.originalFilename || (meta as any)?.filename || (meta as any)?.name || name;
            url = (meta as any)?.downloadUrl || (meta as any)?.url || url;
        } catch {
            /* ignore individual failures */
        }

        try {
            const presign = await uploadedDocumentsService.getDownloadUrl(id);
            const presignedUrl = presign?.data?.url ?? presign?.url;
            url = typeof presignedUrl === 'string' && presignedUrl ? presignedUrl : url;
        } catch {
            /* ignore; keep fallback */
        }

        const safeName = typeof name === 'string' ? name : id;
        const safeUrl = typeof url === 'string' ? url : fallbackUrl;
        // 21/10/2026: o `id` passa a vir junto. Sem ele a tela exibia o
        // documento mas nao sabia a qual id ele correspondia, entao remover uma
        // linha nao tinha como tirar o id de `additionalDocumentIds` — o
        // documento voltava no proximo carregamento.
        return { id, name: safeName, url: safeUrl };
    };

    const docs = await Promise.all(normalizedIds.map(fetchOne));
    return docs.filter(Boolean) as AdditionalDocumentEntry[];
};

/**
 * Reidrata `additionalDocuments` a partir dos ids do registro.
 *
 * 21/10/2026: o registro que o servidor devolve tem `additionalDocumentIds`,
 * nunca `additionalDocuments` — a lista exibida so era montada no loadForEdit.
 * Depois de SALVAR, a tela recebia o registro cru e a secao aparecia vazia:
 * "Nenhum documento adicional adicionado". O orgao de Pato Branco anexou os
 * mesmos arquivos varias vezes achando que o salvamento nao tinha pegado, e
 * acabou com ETP e TR duplicados numa licitacao em producao.
 *
 * Falha de rede aqui nao e fatal: mantem os ids e segue, igual ao loadForEdit.
 */
const hydrateAdditionalDocuments = async <T>(record: T): Promise<T> => {
    const ids = (record as any)?.additionalDocumentIds as string[] | undefined;
    if (!Array.isArray(ids) || ids.length === 0) return record;
    const jaCarregado = Array.isArray((record as any)?.additionalDocuments)
        && (record as any).additionalDocuments.length > 0;
    if (jaCarregado) return record;
    try {
        (record as any).additionalDocuments = await fetchAdditionalDocuments(ids);
    } catch {
        /* mantem so os ids, como o loadForEdit faz */
    }
    return record;
};

function normalizeRecord(r: any): PublicTenderRecord {
    const statusKey = normalizeTenderStatus(r.status);
    const modalityKey = normalizeTenderModality(r.modality);
    const statusSlug = StringUtils.slugify(String(r.status ?? statusKey ?? ''));
    const modalitySlug = StringUtils.slugify(String(r.modality ?? modalityKey ?? ''));
    const active = resolveActiveFlag({ ...r, statusKey });

    return {
        id: String(r.id || r._id).replace(/^PUBLICTENDER:/, ''),
        contractingAgencyId: ValueUtils.emptyToUndefined(r.contractingAgencyId?.toString?.() ?? r.contractingAgencyId),
        contractingAgency: r.contractingAgency,
        title: r.title,
        description: r.description,
        yearOfReference: NumberUtils.toNumberOrUndefined(r.yearOfReference),
        regulationUsed: r.regulationUsed,
        publicTenderCode: r.publicTenderCode,
        administrativeProcessCode: r.administrativeProcessCode,
        pcaReference: r.pcaReference,
        status: r.status,
        contractType: r.contractType,
        fundingSource: r.fundingSource,
        modality: r.modality,
        judgementCriteria: r.judgementCriteria,
        technicalScoreWeight: NumberUtils.toNumberOrUndefined(r.technicalScoreWeight),
        priceScoreWeight: NumberUtils.toNumberOrUndefined(r.priceScoreWeight),
        disputeType: r.disputeType,
        disputeStartMode: r.disputeStartMode, // RN-S1.1.4
        batchSize: r.batchSize, // RN-S1.1.4
        supplierRegionalityBenefit: mapBenefit(REGIONALITY_EN_TO_PT, r.supplierRegionalityBenefit),
        supplierSizeBenefit: mapBenefit(SIZE_EN_TO_PT, r.supplierSizeBenefit),
        regionalPriorityMarginPercent: NumberUtils.toNumberOrUndefined(r.regionalPriorityMarginPercent),
        regionalPriorityMunicipalities: Array.isArray(r.regionalPriorityMunicipalities)
            ? r.regionalPriorityMunicipalities.map((x: any) => String(x))
            : (typeof r.regionalPriorityMunicipalities === 'string' && r.regionalPriorityMunicipalities.trim()
                ? r.regionalPriorityMunicipalities.split(/[\n,;]/).map((s: string) => s.trim()).filter(Boolean)
                : undefined),
        bidDecimalPlaces: NumberUtils.toNumberOrUndefined(r.bidDecimalPlaces),
        proposalValidityMonths: NumberUtils.toNumberOrUndefined(r.proposalValidityMonths),
        isHiddenBudget: r.isHiddenBudget === undefined ? undefined : Boolean(r.isHiddenBudget),
        requiresProposalGuarantee: r.requiresProposalGuarantee === true,
        isPhaseInversion: r.isPhaseInversion === undefined ? undefined : Boolean(r.isPhaseInversion),
        appealPhaseMode: r.appealPhaseMode,
        // Marcos da inversao. Ate 14/09 nao eram mapeados: chegavam sempre
        // undefined, o que fazia o botao "Tornar documentos publicos" continuar
        // aparecendo depois de acionado (inofensivo, o ato e idempotente) e o de
        // "Tornar propostas publicas" NUNCA aparecer, porque ele exige o
        // primeiro marco preenchido.
        suppliersDisclosedAt: r.suppliersDisclosedAt ?? undefined,
        proposalsDisclosedAt: r.proposalsDisclosedAt ?? undefined,
        documentSubmissionMode: r.documentSubmissionMode,
        documentSubmissionDeadlineHours: NumberUtils.toNumberOrUndefined(r.documentSubmissionDeadlineHours),
        exemptionLegalBasis: r.exemptionLegalBasis,
        exemptionJustification: r.exemptionJustification,
        exemptionBidWindowHours: NumberUtils.toNumberOrUndefined(r.exemptionBidWindowHours),
        exemptionDisputeMode: r.exemptionDisputeMode,
        exemptionSimplifiedQualification: r.exemptionSimplifiedQualification === undefined ? undefined : Boolean(r.exemptionSimplifiedQualification),
        accreditationEndDate: r.accreditationEndDate,
        // ATENCAO: normalizeRecord e ALLOWLIST. Campo que nao estiver aqui e
        // descartado em silencio e nunca chega nas views (foi o bug do
        // pncpPublicUrl documentado no fim desta funcao).
        broadcastEnabled: r.broadcastEnabled === undefined ? undefined : Boolean(r.broadcastEnabled),
        broadcastUrl: r.broadcastUrl,
        broadcastRoomName: r.broadcastRoomName,
        broadcastLivestreamUrl: r.broadcastLivestreamUrl,
        broadcastRecordingUrl: r.broadcastRecordingUrl,
        publishedDate: r.publishedDate,
        estimatedValue: NumberUtils.toNumberOrUndefined(r.estimatedValue),
        proposalSubmissionStartDate: r.proposalSubmissionStartDate ?? r.submissionStartDate ?? r.submissionStartAt,
        proposalSubmissionEndDate: r.proposalSubmissionEndDate ?? r.submissionDeadline,
        submissionDeadline: r.submissionDeadline,
        clarificationEndDate: r.clarificationEndDate,
        impeachmentEndDate: r.impeachmentEndDate,
        disputeStartDate: r.disputeStartDate ?? r.openingDate,
        openingDate: r.openingDate,
        appealManifestationTime: NumberUtils.toNumberOrUndefined(r.appealManifestationTime),
        appealEndDate: r.appealEndDate,
        appealReceptionFormTimeHours: NumberUtils.toNumberOrUndefined(r.appealReceptionTimeHours ?? r.appealReceptionFormTimeHours),
        counterArgumentsReceptionFormTimeHours: NumberUtils.toNumberOrUndefined(r.counterArgumentsReceptionTimeHours ?? r.counterArgumentsReceptionFormTimeHours),
        supportTeam: Array.isArray(r.supportTeam) ? r.supportTeam.filter((s: any): s is string => typeof s === 'string' && s.trim().length > 0) : [],
        auctioneer: r.auctioneer,
        superiorAuthority: r.superiorAuthority,
        contactPhone: r.contactPhone,
        contactEmail: r.contactEmail,
        noticeFileName: r.noticeFileName,
        mainDocumentId: ValueUtils.emptyToUndefined(r.mainDocumentId?.toString?.() ?? r.mainDocumentId),
        additionalDocumentIds: toStringList(r.additionalDocumentIds),
        requiredProposalDocuments: toStringList(r.requiredProposalDocuments),
        publishedAt: r.publishedAt,
        republicationMode: typeof r.republicationMode === 'boolean' ? r.republicationMode : undefined,
        proposalCount: NumberUtils.toNumberOrUndefined(r.proposalCount),
        viewCount: NumberUtils.toNumberOrUndefined(r.viewCount),
        daysUntilDeadline: NumberUtils.toNumberOrUndefined(r.daysUntilDeadline),
        urgency: r.urgency,
        formattedValue: r.formattedValue,
        canSubmitProposal: typeof r.canSubmitProposal === 'boolean' ? r.canSubmitProposal : undefined,
        active,
        isUrgent: typeof r.isUrgent === 'boolean' ? r.isUrgent : undefined,
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
        createdBy: r.createdBy,
        createdByName: r.createdByName,
        createdByUser: r.createdByUser,
        updatedBy: r.updatedBy,
        updatedByName: r.updatedByName,
        updatedByUser: r.updatedByUser,
        statusKey,
        statusSlug,
        modalityKey,
        modalitySlug,
        // Status da publicacao PNCP anexado pelo backend (GET /publictenders/:id).
        // Sem pass-through aqui o whitelist descartava e a tela caia em
        // "Aguardando integracao" mesmo com a contratacao publicada no PNCP.
        pncpCode: r.pncpCode,
        pncpPublicUrl: r.pncpPublicUrl,
        pncpIntegrationStatus: r.pncpIntegrationStatus,
    };
}

/**
 * Sessao virtual (Chamamento Publico 052/2026 Anapolis/GO, itens 17.3.2 e
 * 17.5.2): os tres campos que o orgao ajusta direto no painel da Sessao
 * Publica. Link vazio = sem link.
 */
export interface TenderBroadcastSettings {
    /** Liga a sala de video da sessao (gerada pela plataforma no JaaS). */
    broadcastEnabled: boolean;
    /** Link do livestream publico (YouTube) por onde cidadaos e visitantes assistem. */
    broadcastLivestreamUrl: string;
    /** Link da gravacao. Preenchido pelo backend (webhook do JaaS) ou registrado a mao. */
    broadcastRecordingUrl: string;
}

/** Campanha de divulgacao do edital (GET /publictenders/:id/publication-broadcast). */
export interface TenderPublicationBroadcastCampaign {
    id: string;
    tenderId: string;
    /** pending | running | done | disabled */
    status: string;
    origin: 'publish' | 'resend' | string;
    total: number;
    sent: number;
    failed: number;
    /** Fora da lista de teste do ambiente (nao enviados de proposito). */
    skipped?: number;
    excludedOptOut: number;
    createdAt: string;
    finishedAt?: string;
    failedRecipients: Array<{ email: string; name: string; error?: string }>;
}

export interface TenderPublicationBroadcastStatus {
    enabled: boolean;
    campaigns: TenderPublicationBroadcastCampaign[];
}

/**
 * Entrada na sala de video (GET /publictenders/:id/broadcast-token). O JWT e
 * assinado pelo backend, restrito a esta sala, e expira em `expiresAt`.
 */
export interface TenderBroadcastAccess {
    domain: string;
    appId: string;
    roomName: string;
    roomUrl: string;
    externalApiUrl: string;
    jwt: string;
    moderator: boolean;
    expiresAt: string;
}

/**
 * Transmissao ao vivo e contratada por MUNICIPIO (16/09/2026: somente Anapolis).
 * `available=false` traz o motivo legivel do backend; a tela esconde o interruptor
 * de ligar a sala e mostra que a funcionalidade e sob contratacao.
 */
export interface TenderBroadcastAvailability {
    available: boolean;
    reason: string;
}

class PublicTendersService extends BaseService<PublicTenderRecord, PublicTenderCreateDto, PublicTenderUpdateDto, PublicTenderFilters> {
    constructor() {
        super({
            basePath: '/publictenders',
            normalizeRecord,
            mapPaginated: (raw) => {
                const source = Array.isArray(raw?.items)
                    ? raw.items
                    : Array.isArray(raw?.data)
                    ? raw.data
                    : Array.isArray(raw)
                    ? raw
                    : [];
                return {
                    items: source.map(normalizeRecord),
                    pagination: raw?.pagination ?? raw?.meta?.pagination,
                    summary: raw?.summary,
                    appliedFilters: raw?.appliedFilters ?? raw?.applied_filters,
                };
            },
        });
    }

    async details(id: string): Promise<TenderDetailsAggregate> {
        const { http } = await import('@/chassis/data/services/http-client');
        const primaryId = String(normalizePrimaryResourceId(id));
        const data = await http.get(`${this.basePath}/${encodeURIComponent(primaryId)}`);

        const tender = data.tender ? normalizeRecord(data.tender) : normalizeRecord(data);
        return { tender, related: data.related, summary: data.summary };
    }

    /**
     * FIX19 (backlog 20/08, item 6) — liga/desliga o modo REPUBLICACAO na
     * licitacao suspensa.
     *
     * O backend ja tinha os dois regimes desde 14/08 (FIX18): suspensa sem a
     * flag e RETOMADA, que so deixa mexer em objeto, documentos adicionais e
     * equipe; com a flag e REPUBLICACAO, que libera tudo — datas de fase ja
     * ocorrida e lotes/itens inclusive. Faltava a tela ligar a chave, entao
     * toda edicao de suspensa caia na retomada e a mensagem de erro mandava o
     * usuario clicar num botao que nao existia:
     *
     *   "DATA_DE_FASE_OCORRIDA: Data de publicacao pertence a fase que JA
     *    ocorreu" (update-publictender.use-case.ts:409)
     *   "Para alterar lotes/itens, use 'Republicar edital'"
     *    (lot-editability.applicationservice.ts:61)
     *
     * A flag e consumida sozinha: suspender e retomar gravam `false`
     * (suspendtender.use-case.ts:139, resumetender.use-case.ts:228), entao ela
     * nunca sobra ligada de um ciclo para o outro.
     */
    async setRepublicationMode(id: string, enabled: boolean): Promise<PublicTenderRecord> {
        // Passa pelo update padrao de proposito: herda normalizacao de id,
        // headers e tratamento de erro do BaseService.
        return this.update(id as any, { republicationMode: enabled } as any);
    }

    async publish(id: string, payload: { channel?: string; notes?: string } = {}): Promise<any> {
        const { http } = await import('@/chassis/data/services/http-client');
        const primaryId = String(normalizePrimaryResourceId(id));
        return http.post(`${this.basePath}/${primaryId}/publish`, payload);
    }

    /**
     * Contagem de lotes e itens por tender, em batch (1 request por página de
     * lista) — GET /publictenders/lot-item-counts. Usado pela busca de
     * processos e pelo painel ("N lotes · M itens" no card). Falha devolve
     * mapa vazio: a contagem é cosmética e não pode quebrar a listagem.
     */
    async getLotItemCounts(ids: string[]): Promise<Record<string, { lots: number; items: number }>> {
        const cleaned = Array.from(
            new Set(
                ids
                    .map((id) => String(id ?? '').replace(/^PUBLICTENDER:/i, '').trim())
                    .filter(Boolean),
            ),
        );
        if (!cleaned.length) return {};
        try {
            const { http } = await import('@/chassis/data/services/http-client');
            const response = await http.get<{ counts?: Record<string, { lots: number; items: number }> }>(
                `${this.basePath}/lot-item-counts?ids=${encodeURIComponent(cleaned.join(','))}`,
            );
            return response?.counts ?? {};
        } catch {
            return {};
        }
    }

    /**
     * Cria um pregão de teste completo (tender + 2 lotes + itens + propostas mock).
     * Endpoint dev-only — backend retorna 404 em produção.
     * Ver `cdcb-backend/src/.../dev-seed-tender.controller.ts` para parâmetros.
     */
    async seedDev(payload: {
        proposalDurationMinutes?: 1 | 3 | 5 | 10;
        startMode?: 'AUTO' | 'MANUAL';
        batchSize?: number | 'ALL';
        modality?: string;
        judgementCriteria?: string;
        disputeType?: string;
        isPhaseInversion?: boolean;
        meEppTieRate?: number;
        preferPregoeiroUserId?: string;
        lot1Benefit?: 'NONE' | 'ME_EPP_COOP_PREFERENCE' | 'ME_EPP_COOP_EXCLUSIVE';
        lot2Benefit?: 'NONE' | 'ME_EPP_COOP_PREFERENCE' | 'ME_EPP_COOP_EXCLUSIVE';
        lotsConfig?: { none?: number; meEppPreference?: number; meEppExclusive?: number };
        withMockProposals?: boolean;
        withMockBids?: boolean;
    } = {}): Promise<{
        success: boolean;
        tenderId: string;
        tenderUuid: string;
        tenderCode: string;
        lots: Array<{ lotId: string; itemsCount: number; estimatedValue: number }>;
        proposalsCreated: number;
        meEppTieArmedLots?: string[];
        redirectPath: string;
    }> {
        const { http } = await import('@/chassis/data/services/http-client');
        return http.post('/dev/seed-tender', payload);
    }

    /**
     * Dev-only (14/07): uma rodada de lances simulados nos pregões [TESTE] em
     * disputa. BE usa o use-case real de lance — SSE/extensões/validações reais.
     */
    async devSimulateBids(payload: { tenderId?: string } = {}): Promise<{
        success: boolean;
        lots: number;
        bidsCreated: number;
        rejected: Array<{ lot: string; supplier: string; reason: string }>;
    }> {
        const { http } = await import('@/chassis/data/services/http-client');
        return http.post('/dev/simulate-bids', payload);
    }

    /**
     * Dispara um tick manual do TenderStatusLifecycleService no backend.
     * Endpoint dev-only — contorna o BOOTSTRAP_DELAY_MS=5min do scheduler
     * pra avançar pregões de teste sem esperar.
     */
    async devLifecycleTick(): Promise<{ success: boolean; elapsedMs: number; triggeredAt: string }> {
        const { http } = await import('@/chassis/data/services/http-client');
        return http.post('/dev/lifecycle-tick', {});
    }

    async submitProposal(tenderId: string, payload: Record<string, any>): Promise<any> {
        const { http } = await import('@/chassis/data/services/http-client');
        const primaryId = String(normalizePrimaryResourceId(tenderId));
        return http.post(`${this.basePath}/${primaryId}/proposals/submit`, payload);
    }

    async evaluateProposals(tenderId: string, payload: { criteria: string[]; autoClassify?: boolean }): Promise<any> {
        const { http } = await import('@/chassis/data/services/http-client');
        const primaryId = String(normalizePrimaryResourceId(tenderId));
        return http.put(`${this.basePath}/${primaryId}/proposals/evaluate`, payload);
    }

    // ── Lifecycle operations (use-cases dedicados) ─────────────────

    /** Suspender licitação — PATCH /publictenders/:id/suspend */
    async suspend(tenderId: string, justification: string, extras?: {
        /** Item 8 (19/09/2026): previsão de retomada digitada a mão (ISO yyyy-mm-dd). */
        expectedResumeDate?: string;
        /** Item 8: suspensão sine die, sem data de retomada definida. Exclusivo com a data. */
        sineDie?: boolean;
    }): Promise<any> {
        const { http } = await import('@/chassis/data/services/http-client');
        const primaryId = String(normalizePrimaryResourceId(tenderId));
        return http.patch(`${this.basePath}/${primaryId}/suspend`, {
            suspensionReason: justification,
            ...(extras?.expectedResumeDate ? { expectedResumeDate: extras.expectedResumeDate } : {}),
            ...(extras?.sineDie ? { sineDie: true } : {}),
        });
    }

    /** Retomar licitação suspensa — PATCH /publictenders/:id/resume */
    async resume(tenderId: string, justification: string, extras?: {
        /** T12b (Art. 55 §1º): nova data-limite de propostas quando as alterações da suspensão afetam as propostas. */
        newProposalSubmissionEndDate?: string;
        newDisputeStartDate?: string;
    }): Promise<any> {
        const { http } = await import('@/chassis/data/services/http-client');
        const primaryId = String(normalizePrimaryResourceId(tenderId));
        return http.patch(`${this.basePath}/${primaryId}/resume`, {
            resumeJustification: justification,
            ...(extras ?? {}),
        });
    }

    /** Cancelar licitação — PATCH /publictenders/:id/cancel */
    async cancel(tenderId: string, justification: string): Promise<any> {
        const { http } = await import('@/chassis/data/services/http-client');
        const primaryId = String(normalizePrimaryResourceId(tenderId));
        return http.patch(`${this.basePath}/${primaryId}/cancel`, {
            cancellationReason: justification,
        });
    }

    /**
     * Sessao virtual: PUT /publictenders/:id PARCIAL com SOMENTE
     * broadcastEnabled, broadcastLivestreamUrl e broadcastRecordingUrl.
     *
     * Nao usa update()/submitUpdate() de proposito: esses mandam o registro
     * inteiro (ou userId junto), e o guard de edital publicado do controller
     * recusa o PUT se qualquer outro campo vier diferente do gravado. Os tres
     * estao em ALLOWED_WHEN_PUBLISHED, entao o link da gravacao entra com a
     * licitacao em qualquer fase; o backend ainda aplica
     * assertUserBelongsToTenderAgency (so usuario do orgao altera).
     *
     * Link vazio vai como string vazia: e assim que o campo e limpo (o DTO do
     * chassis repassa o valor e a entidade grava '' no campo Url).
     */
    async updateBroadcastSettings(tenderId: string, settings: TenderBroadcastSettings): Promise<any> {
        const { http } = await import('@/chassis/data/services/http-client');
        const primaryId = String(normalizePrimaryResourceId(tenderId));
        // A sala (broadcastUrl/broadcastRoomName) NAO vai no payload: e o backend que a
        // gera no JaaS ao ligar broadcastEnabled. O que o pregoeiro registra e so o
        // livestream publico e, se precisar, a gravacao.
        const payload: TenderBroadcastSettings = {
            broadcastEnabled: settings.broadcastEnabled === true,
            broadcastLivestreamUrl: String(settings.broadcastLivestreamUrl ?? '').trim(),
            broadcastRecordingUrl: String(settings.broadcastRecordingUrl ?? '').trim(),
        };
        return http.put(`${this.basePath}/${encodeURIComponent(primaryId)}`, payload);
    }

    /**
     * Divulgacao do edital por e-mail (todos os fornecedores, na primeira publicacao).
     * So a equipe do orgao consulta; o backend responde 403 para os demais.
     */
    async getPublicationBroadcast(tenderId: string): Promise<TenderPublicationBroadcastStatus> {
        const { http } = await import('@/chassis/data/services/http-client');
        const primaryId = String(normalizePrimaryResourceId(tenderId));
        const raw = await http.get<any>(`${this.basePath}/${encodeURIComponent(primaryId)}/publication-broadcast`);
        const data = raw?.data ?? raw;
        if (!data || typeof data.enabled !== 'boolean' || !Array.isArray(data.campaigns)) {
            throw new Error('Resposta inesperada do servidor ao consultar a divulgação do edital.');
        }
        return data as TenderPublicationBroadcastStatus;
    }

    /** Reenvia a divulgacao SO para quem falhou. 409 quando nao ha falhas ou a feature esta desligada. */
    async resendPublicationBroadcast(tenderId: string): Promise<{ queued: number }> {
        const { http } = await import('@/chassis/data/services/http-client');
        const primaryId = String(normalizePrimaryResourceId(tenderId));
        const raw = await http.post<any>(`${this.basePath}/${encodeURIComponent(primaryId)}/publication-broadcast/resend`, {});
        const data = raw?.data ?? raw;
        return { queued: Number(data?.queued ?? 0) };
    }

    /**
     * Token de entrada na sala de video da sessao (JaaS). So orgao da licitacao e
     * fornecedor com proposta recebem; o backend responde 403 para os demais e 409
     * com a transmissao desligada. Sem fallback: erro sobe para o painel mostrar.
     */
    async getBroadcastAccess(tenderId: string): Promise<TenderBroadcastAccess> {
        const { http } = await import('@/chassis/data/services/http-client');
        const primaryId = String(normalizePrimaryResourceId(tenderId));
        const raw = await http.get<any>(`${this.basePath}/${encodeURIComponent(primaryId)}/broadcast-token`);
        const data = raw?.data ?? raw;
        if (!data?.jwt || !data?.roomName || !data?.externalApiUrl) {
            throw new Error('Resposta inesperada do servidor ao pedir acesso à sala de vídeo.');
        }
        return data as TenderBroadcastAccess;
    }

    /**
     * Disponibilidade da transmissao ao vivo para o ORGAO (nao para a licitacao: o
     * cadastro ainda nao tem id, mas ja sabe o orgao). Sem fallback: erro sobe.
     */
    async getBroadcastAvailability(agencyId: string): Promise<TenderBroadcastAvailability> {
        const { http } = await import('@/chassis/data/services/http-client');
        const bareAgencyId = String(agencyId || '').split(':').pop() || '';
        if (!bareAgencyId) throw new Error('Órgão não informado para consultar a transmissão ao vivo.');
        const raw = await http.get<any>(`${this.basePath}/broadcast-availability/${encodeURIComponent(bareAgencyId)}`);
        const data = raw?.data ?? raw;
        if (!data || typeof data.available !== 'boolean') {
            throw new Error('Resposta inesperada do servidor ao consultar a transmissão ao vivo.');
        }
        return { available: data.available, reason: String(data.reason ?? '') };
    }
}

const requiredValidationRules = buildRequiredRules<PublicTenderCoreFields>(
    FIELD_CONFIG,
    (_key, cfg) => `O campo precisa ser preenchido: ${cfg.label}`,
);

// Form service class using BaseFormService for form state management
export class PublicTendersFormService extends BaseFormService<
    PublicTenderRecord,
    PublicTenderCoreFields,
    PublicTenderCreateDto,
    PublicTenderUpdateDto
> {
    async submitCreate(formData: PublicTenderCoreFields): Promise<PublicTenderRecord> {
        const created = await super.submitCreate(formData);
        const uploadedMain = await uploadMainTenderDocument(created.id, formData.noticeDocument);
        const uploadedMainDocumentId = resolveUploadedMainDocumentId(uploadedMain);

        if (uploadedMainDocumentId) {
            await linkMainDocumentToTender(created.id, uploadedMainDocumentId, formData.noticeDocument?.name ?? formData.noticeFileName);
        }

        const record = formData.noticeDocument?.file ? await this.get(created.id) : created;
        return hydrateAdditionalDocuments(record);
    }

    async submitUpdate(id: string | number, formData: Partial<PublicTenderCoreFields>): Promise<PublicTenderRecord> {
        const updated = await super.submitUpdate(id, formData);
        const previousMainDocumentId = StringUtils.toOptionalTrimmed(formData.previousMainDocumentId);
        const uploadedMain = await uploadMainTenderDocument(id, formData.noticeDocument);
        const uploadedMainDocumentId = resolveUploadedMainDocumentId(uploadedMain);

        if (uploadedMainDocumentId) {
            await linkMainDocumentToTender(id, uploadedMainDocumentId, formData.noticeDocument?.name ?? formData.noticeFileName);
        }

        const shouldDeletePreviousMain = shouldDeletePreviousMainDocument(
            previousMainDocumentId,
            formData.removeNoticeDocument,
            Boolean(formData.noticeDocument?.file),
            uploadedMainDocumentId,
        );

        if (shouldDeletePreviousMain) {
            await deleteTenderDocument(id, previousMainDocumentId);
        }

        if (shouldUnlinkMainDocument(uploadedMainDocumentId, formData.removeNoticeDocument)) {
            await unlinkMainDocumentFromTender(id);
        }

        const record = shouldDeletePreviousMain || formData.noticeDocument?.file || Boolean(uploadedMainDocumentId)
            ? await this.get(id)
            : updated;
        return hydrateAdditionalDocuments(record);
    }

    async loadForEdit(id: string | number): Promise<FormState<PublicTenderCoreFields>> {
        const cleanId = typeof id === 'string' ? id.replace(/^PUBLICTENDER:/, '') : id;
        const state = await super.loadForEdit(cleanId);
        const ids = (state.data as any)?.additionalDocumentIds as string[] | undefined;
        const alreadyLoaded = Array.isArray((state.data as any)?.additionalDocuments) && (state.data as any).additionalDocuments?.length > 0;

        if (ids && ids.length && !alreadyLoaded) {
            try {
                const docs = await fetchAdditionalDocuments(ids);
                (state.data as any).additionalDocuments = docs as any;
            } catch {
                /* ignore fetch failures; keep IDs only */
            }
        }

        return state;
    }

    constructor() {
        super({
            basePath: '/publictenders',
            initialData: publicTenderFormDefaults,
            validationRules: {
                ...requiredValidationRules,
                title: [...(requiredValidationRules.title || []), ValidationRules.minLength(3)],
                // Art. 40 §1° Lei 14.133/2021 — piso mínimo de 10 minutos
                // para manifestação de intenção de recurso. Reforço no frontend
                // (backend tem floor equivalente em entity validation).
                // Mensagem i18n resolvida na BaseFormService: usa a chave
                // 'publicTenders.fields.appealManifestationTime.validation.min10'.
                appealManifestationTime: [
                    ValidationRules.min(10, i18n['publicTenders.fields.appealManifestationTime.validation.min10'].pt),
                ],
            },
            transformForCreate: sanitizePublicTenderPayload,
            transformForUpdate: (form: Partial<PublicTenderCoreFields>, _id: string | number) => sanitizePublicTenderPayload(form as PublicTenderCoreFields),
            transformToForm: (record) => ({
                ...publicTenderFormDefaults,
                contractingAgencyId: record.contractingAgencyId,
                title: record.title,
                description: record.description,
                yearOfReference: NumberUtils.toNumberOrUndefined(record.yearOfReference),
                regulationUsed: record.regulationUsed,
                publicTenderCode: record.publicTenderCode,
                administrativeProcessCode: record.administrativeProcessCode,
                status: record.status,
                contractType: record.contractType,
                fundingSource: record.fundingSource,
                modality: record.modality,
                judgementCriteria: record.judgementCriteria,
                disputeType: record.disputeType,
                disputeStartMode: record.disputeStartMode ?? 'AUTO', // RN-S1.1.4: backward compat
                batchSize: record.batchSize ?? 'ALL', // RN-S1.1.4: backward compat
                supplierRegionalityBenefit: mapBenefit(REGIONALITY_EN_TO_PT, record.supplierRegionalityBenefit) ?? supplierRegionalityBenefitOptions[0]?.value,
                supplierSizeBenefit: mapBenefit(SIZE_EN_TO_PT, record.supplierSizeBenefit) ?? supplierSizeBenefitOptions[0]?.value,
                regionalPriorityMarginPercent: NumberUtils.toNumberOrUndefined(record.regionalPriorityMarginPercent),
                regionalPriorityMunicipalities: ((): string[] | undefined => {
                    const raw: any = (record as any).regionalPriorityMunicipalities;
                    if (Array.isArray(raw)) return raw.map((x: any) => String(x));
                    if (typeof raw === 'string' && raw.trim()) return raw.split(/[\n,;]/).map((s: string) => s.trim()).filter(Boolean);
                    return undefined;
                })(),
                bidDecimalPlaces: NumberUtils.toNumberOrUndefined(record.bidDecimalPlaces),
                proposalValidityMonths: NumberUtils.toNumberOrUndefined(record.proposalValidityMonths),
                technicalScoreWeight: NumberUtils.toNumberOrUndefined(record.technicalScoreWeight),
                priceScoreWeight: NumberUtils.toNumberOrUndefined(record.priceScoreWeight),
                isHiddenBudget: Boolean(record.isHiddenBudget),
                requiresProposalGuarantee: Boolean(record.requiresProposalGuarantee),
                publishedDate: record.publishedDate ?? record.publishedAt,
                proposalSubmissionStartDate: record.proposalSubmissionStartDate,
                proposalSubmissionEndDate: record.proposalSubmissionEndDate ?? record.submissionDeadline,
                clarificationEndDate: record.clarificationEndDate,
                impeachmentEndDate: record.impeachmentEndDate,
                disputeStartDate: record.disputeStartDate ?? record.openingDate,
                appealManifestationTime: NumberUtils.toNumberOrUndefined(record.appealManifestationTime),
                appealEndDate: record.appealEndDate,
                appealReceptionFormTimeHours: NumberUtils.toNumberOrUndefined((record as any).appealReceptionTimeHours ?? record.appealReceptionFormTimeHours),
                counterArgumentsReceptionFormTimeHours: NumberUtils.toNumberOrUndefined((record as any).counterArgumentsReceptionTimeHours ?? record.counterArgumentsReceptionFormTimeHours),
                supportTeam: Array.isArray(record.supportTeam) ? record.supportTeam.filter((s: any): s is string => typeof s === 'string' && s.trim().length > 0) : [],
                auctioneer: record.auctioneer,
                superiorAuthority: record.superiorAuthority,
                contactPhone: record.contactPhone,
                contactEmail: record.contactEmail,
                noticeFileName: record.noticeFileName,
                persistedNoticeFileName: record.noticeFileName,
                mainDocumentId: record.mainDocumentId,
                previousMainDocumentId: record.mainDocumentId,
                removeNoticeDocument: false,
                additionalDocumentIds: toStringList(record.additionalDocumentIds) ?? [],
                additionalDocuments: Array.isArray(record.additionalDocuments)
                    ? record.additionalDocuments
                          .map((doc) => ({
                              name: StringUtils.toOptionalTrimmed((doc as any).name) as any,
                          }))
                          .filter((doc) => doc.name)
                    : [],
                isPhaseInversion: Boolean(record.isPhaseInversion),
                // Registros anteriores ao campo nao tem valor: caem em SINGLE, que
                // e o comportamento que eles ja tinham.
                appealPhaseMode: (record as any).appealPhaseMode || 'SINGLE',
                documentSubmissionMode: record.documentSubmissionMode || 'FASE_JULGAMENTO',
                documentSubmissionDeadlineHours: NumberUtils.toNumberOrUndefined(record.documentSubmissionDeadlineHours),
                exemptionLegalBasis: record.exemptionLegalBasis,
                exemptionJustification: record.exemptionJustification,
                exemptionBidWindowHours: NumberUtils.toNumberOrUndefined(record.exemptionBidWindowHours),
                exemptionDisputeMode: (record as any).exemptionDisputeMode,
                exemptionSimplifiedQualification: Boolean(record.exemptionSimplifiedQualification),
                accreditationEndDate: (record as any).accreditationEndDate,
                broadcastEnabled: Boolean((record as any).broadcastEnabled),
                broadcastUrl: (record as any).broadcastUrl,
                broadcastLivestreamUrl: (record as any).broadcastLivestreamUrl,
                broadcastRecordingUrl: (record as any).broadcastRecordingUrl,
                requiredProposalDocuments: toStringList(record.requiredProposalDocuments) ?? [],
                active: resolveActiveFlag(record, publicTenderFormDefaults.active),
                createdAt: record.createdAt,
                updatedAt: record.updatedAt,
                createdBy: record.createdBy,
                createdByName: record.createdByName,
                createdByUser: record.createdByUser,
                updatedBy: record.updatedBy,
                updatedByName: record.updatedByName,
                updatedByUser: record.updatedByUser,
            }),
        });
    }
}

// Singleton instances
export const publicTendersService = new PublicTendersService();
export const createPublicTendersFormService = () => new PublicTendersFormService();

const normalizeEnvBoolean = (value: unknown, defaultValue = true): boolean => {
    if (value === undefined || value === null || value === '') {
        return defaultValue;
    }

    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value === 1;
    }

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
        if (['false', '0', 'no', 'n'].includes(normalized)) return false;
    }

    return defaultValue;
};

const pncpIntegrationEnabled = normalizeEnvBoolean(
    (import.meta as any)?.env?.VITE_PNCP_INTEGRATION_ENABLED,
    false
);

/**
 * Busca o registro de publicacao PNCP para uma licitacao especifica.
 * Retorna null se nao existir nenhuma publicacao cadastrada.
 */
export interface PncpPublicationInfo {
    id: string;
    localEntityId: string;
    entityType: string;
    currentStatus: string;
    pncpPublicUrl?: string;
    numeroControlePncp?: string;
    sequencialPncp?: number;
    anoPncp?: number;
    publishedAt?: string;
    cnpjOrgao?: string;
}

export const fetchPncpPublication = async (tenderId: string): Promise<PncpPublicationInfo | null> => {
    if (!tenderId) return null;
    if (!pncpIntegrationEnabled) {
        return null;
    }
    const cleanTenderId = tenderId.replace(/^PUBLICTENDER:/, '');
    try {
        const { pncpPublicationsService } = await import('@/domains/pages/6.1-integrations-module-publictender/PNCPPublications/service');
        const response = await pncpPublicationsService.list({
            localEntityId: cleanTenderId,
            entityType: 'contratacao',
            limit: 1,
            page: 1,
        } as any);
        const items: any[] = response?.items ?? [];
        if (!items.length) return null;
        const pub = items[0];
        return {
            id: pub.id ?? pub._id,
            localEntityId: pub.localEntityId,
            entityType: pub.entityType,
            currentStatus: pub.currentStatus,
            pncpPublicUrl: pub.pncpPublicUrl ?? undefined,
            numeroControlePncp: pub.numeroControlePncp ?? undefined,
            sequencialPncp: pub.sequencialPncp ?? undefined,
            anoPncp: pub.anoPncp ?? undefined,
            publishedAt: pub.publishedAt ?? undefined,
            cnpjOrgao: pub.cnpjOrgao ?? undefined,
        };
    } catch {
        return null;
    }
};
